/**
 * FirmBranding.tsx
 *
 * Admin-only panel for customizing firm branding.
 * Renders in Admin → Branding sub-tab.
 *
 * Saves to PATCH /firms/me/branding
 * Reads from GET /firms/me
 *
 * Changes take effect on next page load — the Dashboard fetches
 * firm config on mount and passes it down as props.
 */

import React, { useEffect, useState } from 'react';

interface FirmBrandingProps {
  token: string;
  onBrandingUpdate?: (primary: string, accent: string, name: string) => void;
}

const API = 'https://coast-terminal-production.up.railway.app';

const PRESET_COLORS = [
  { label: 'Coast Gold',     primary: '#C9A96E', accent: '#1A5A8A' },
  { label: 'Sunset',         primary: '#E8854A', accent: '#7A3A6A' },
  { label: 'Dusk Purple',    primary: '#8A5A9A', accent: '#E8854A' },
  { label: 'Deep Ocean',     primary: '#1A5A7A', accent: '#C9A96E' },
  { label: 'Coral',          primary: '#C96A5A', accent: '#1A5A7A' },
  { label: 'Magic Hour',     primary: '#D4956A', accent: '#6A3A8A' },
  { label: 'Midnight Teal',  primary: '#1A6A6A', accent: '#C9A96E' },
  { label: 'Rose Gold',      primary: '#C47A7A', accent: '#4A3A6A' },
];

const FirmBranding: React.FC<FirmBrandingProps> = ({ token, onBrandingUpdate }) => {
  const gold = '#C9A96E';
  const green = '#1A7A4A';
  const red = '#CC2200';
  const bg = '#080d18';
  const surface = '#0e1420';
  const surfaceRaised = '#141d2e';
  const surfaceHigh = '#1a2438';
  const border = '#1e2a3d';
  const borderLight = '#243448';
  const textPrimary = '#E8DCC8';
  const textSecondary = '#7A8CA0';
  const textMuted = '#3A4A5C';
  const serifFont = "'Cormorant Garamond', Georgia, serif";

  const headers = { Authorization: `Bearer ${token}` };

  const [firmName, setFirmName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#C9A96E');
  const [accentColor, setAccentColor] = useState('#1A5A8A');
  const [original, setOriginal] = useState({ firmName: '', logoUrl: '', primaryColor: '', accentColor: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetch(`${API}/firms/me`, { headers })
      .then(r => r.json())
      .then(data => {
        setFirmName(data.name || '');
        setLogoUrl(data.logo_url || '');
        setPrimaryColor(data.primary_color || '#C9A96E');
        setAccentColor(data.accent_color || '#1A5A8A');
        setOriginal({ firmName: data.name || '', logoUrl: data.logo_url || '', primaryColor: data.primary_color || '#C9A96E', accentColor: data.accent_color || '#1A5A8A' });
      })
      .catch(() => setError('Failed to load firm settings.'))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const hasChanges =
    firmName !== original.firmName ||
    logoUrl !== original.logoUrl ||
    primaryColor !== original.primaryColor ||
    accentColor !== original.accentColor;

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API}/firms/me/branding`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: firmName,
          logo_url: logoUrl || null,
          primary_color: primaryColor,
          accent_color: accentColor,
        }),
      });
      if (!res.ok) throw new Error('Failed to save');
      setOriginal({ firmName, logoUrl, primaryColor, accentColor });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      onBrandingUpdate?.(primaryColor, accentColor, firmName);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const inp: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', backgroundColor: surfaceHigh,
    border: `1px solid ${borderLight}`, borderRadius: '2px', padding: '10px 14px',
    color: textPrimary, fontSize: '13px', outline: 'none', fontFamily: 'inherit',
  };

  const btnGold: React.CSSProperties = {
    backgroundColor: gold, color: bg, border: 'none', borderRadius: '2px',
    padding: '10px 28px', fontWeight: 800, fontSize: '11px', letterSpacing: '1.5px',
    textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit',
  };

  const btnOut: React.CSSProperties = {
    backgroundColor: 'transparent', color: textSecondary, border: `1px solid ${borderLight}`,
    borderRadius: '2px', padding: '10px 20px', fontWeight: 700, fontSize: '11px',
    letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit',
  };

  if (loading) return <div style={{ color: textMuted, fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase' }}>Loading...</div>;

  return (
    <div style={{ maxWidth: '680px' }}>
      <div style={{ fontSize: '9px', color: gold, letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '6px', fontWeight: 700 }}>
        Firm Configuration
      </div>
      <div style={{ fontSize: '22px', fontWeight: 300, color: textPrimary, fontFamily: serifFont, marginBottom: '4px' }}>
        Branding & Identity
      </div>
      <div style={{ fontSize: '12px', color: textMuted, marginBottom: '28px' }}>
        Customize your firm's name, logo, and colors. Changes apply immediately on next login.
      </div>

      {error && <div style={{ padding: '10px 14px', marginBottom: '16px', backgroundColor: 'rgba(204,34,0,0.08)', border: `1px solid rgba(204,34,0,0.25)`, color: red, fontSize: '12px' }}>{error}</div>}

      {/* Live preview */}
      <div style={{ marginBottom: '24px', backgroundColor: surface, border: `1px solid ${border}` }}>
        <div style={{ padding: '10px 14px', borderBottom: `1px solid ${border}`, fontSize: '9px', color: textMuted, letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 700 }}>
          Live Preview
        </div>
        <div style={{ padding: '20px 24px', backgroundColor: '#0e1420', borderBottom: `1px solid ${border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '3px', height: '28px', background: `linear-gradient(180deg, ${primaryColor}, ${primaryColor}44)` }} />
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" style={{ height: '32px', objectFit: 'contain' }} onError={() => setLogoUrl('')} />
            ) : (
              <div>
                <div style={{ fontSize: '17px', fontWeight: 800, letterSpacing: '6px', fontFamily: serifFont, color: primaryColor }}>COAST</div>
                <div style={{ fontSize: '8px', color: textMuted, letterSpacing: '2px', textTransform: 'uppercase', marginTop: '-1px' }}>{firmName || 'Your Firm Name'}</div>
              </div>
            )}
          </div>
        </div>
        <div style={{ padding: '14px 24px', display: 'flex', gap: '10px', alignItems: 'center' }}>
          <div style={{ width: '32px', height: '32px', borderRadius: '2px', backgroundColor: primaryColor, border: `1px solid ${border}` }} />
          <div style={{ width: '32px', height: '32px', borderRadius: '2px', backgroundColor: accentColor, border: `1px solid ${border}` }} />
          <div style={{ fontSize: '11px', color: textMuted, marginLeft: '4px' }}>Primary · Accent</div>
        </div>
      </div>

      {/* Firm name */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ fontSize: '9px', color: textMuted, letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 700, marginBottom: '8px' }}>Firm Name</div>
        <input style={inp} value={firmName} onChange={e => setFirmName(e.target.value)} placeholder="Your firm name" maxLength={80} />
      </div>

      {/* Logo URL */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ fontSize: '9px', color: textMuted, letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 700, marginBottom: '8px' }}>Logo URL <span style={{ color: '#2A3A4C', fontWeight: 400, textTransform: 'none', fontSize: '10px', letterSpacing: 0 }}>(optional — leave blank to show COAST wordmark)</span></div>
        <input style={inp} value={logoUrl} onChange={e => setLogoUrl(e.target.value)} placeholder="https://yourfirm.com/logo.png" />
      </div>

      {/* Color presets */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ fontSize: '9px', color: textMuted, letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 700, marginBottom: '12px' }}>Color Presets</div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {PRESET_COLORS.map(preset => (
            <button
              key={preset.label}
              onClick={() => { setPrimaryColor(preset.primary); setAccentColor(preset.accent); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px',
                backgroundColor: primaryColor === preset.primary ? 'rgba(201,169,110,0.1)' : surfaceRaised,
                border: `1px solid ${primaryColor === preset.primary ? gold : border}`,
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              <div style={{ display: 'flex', gap: '3px' }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '1px', backgroundColor: preset.primary }} />
                <div style={{ width: '12px', height: '12px', borderRadius: '1px', backgroundColor: preset.accent }} />
              </div>
              <span style={{ fontSize: '11px', color: primaryColor === preset.primary ? gold : textSecondary, fontWeight: primaryColor === preset.primary ? 700 : 400 }}>{preset.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Custom colors */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '28px' }}>
        <div>
          <div style={{ fontSize: '9px', color: textMuted, letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 700, marginBottom: '8px' }}>Primary Color</div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input type="color" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} style={{ width: '40px', height: '40px', border: `1px solid ${borderLight}`, borderRadius: '2px', cursor: 'pointer', padding: '2px', backgroundColor: surfaceHigh }} />
            <input style={{ ...inp, flex: 1, fontFamily: 'monospace', fontSize: '12px' }} value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} maxLength={7} />
          </div>
        </div>
        <div>
          <div style={{ fontSize: '9px', color: textMuted, letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 700, marginBottom: '8px' }}>Accent Color</div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input type="color" value={accentColor} onChange={e => setAccentColor(e.target.value)} style={{ width: '40px', height: '40px', border: `1px solid ${borderLight}`, borderRadius: '2px', cursor: 'pointer', padding: '2px', backgroundColor: surfaceHigh }} />
            <input style={{ ...inp, flex: 1, fontFamily: 'monospace', fontSize: '12px' }} value={accentColor} onChange={e => setAccentColor(e.target.value)} maxLength={7} />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '16px', borderTop: `1px solid ${border}` }}>
        <button onClick={() => { setFirmName(original.firmName); setLogoUrl(original.logoUrl); setPrimaryColor(original.primaryColor); setAccentColor(original.accentColor); }} disabled={!hasChanges || saving} style={{ ...btnOut, opacity: hasChanges && !saving ? 1 : 0.3, cursor: hasChanges && !saving ? 'pointer' : 'default' }}>
          Reset
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          {saved && <span style={{ fontSize: '11px', color: green, fontWeight: 700 }}>✓ Saved</span>}
          <button onClick={handleSave} disabled={!hasChanges || saving} style={{ ...btnGold, opacity: hasChanges && !saving ? 1 : 0.4, cursor: hasChanges && !saving ? 'pointer' : 'default' }}>
            {saving ? 'Saving...' : 'Save Branding'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FirmBranding;